import { bearing, distance, lineOffset, lineString } from '@turf/turf';
import * as polyline from 'google-polyline';
import { Map } from 'mapbox-gl';
import { SETTINGS } from '../settings';
import {
  Polyline,
  ProcessedElevationPolylines,
  ProcessedPolyline,
  ProcessedPolylineItem,
  RideLike,
  RideListRide,
  RideOrRoute,
  RideOrRouteOrSegment,
  RideOrRouteSegment,
  Vec2,
  WeatherLike,
} from '../types';
import { isActivity, isRideOrRouteSegment, isRoute } from '../types/guards';
import { kphToMetersPerSecond, metersPerSecondToKph, metersToKm } from './units';
import {
  calculateCalories,
  clamp,
  decodePolylineForMapbox,
  easeHardnessInOutQuad,
  getLineAngle,
  getPolylineDistance,
  latLngToPoint,
  resamplePolyline,
  rgbToHex,
} from './utils';

export const getSegmentsFromRideOrRoute = (ride: RideOrRoute) => {
  let segments = [];
  if (isRoute(ride)) {
    segments = ride.segments;
  } else {
    segments = ride.segment_efforts.map(effort => effort.segment);
  }
  return segments;
};

export const getPolylineFromRide = (ride: RideLike | RideOrRoute) => {
  return ride.map?.polyline || ride.map?.summary_polyline || null;
};

// NOTE: Rides have a start_latlng but routes don't, so fall back to the first point of the polyline.
// Works with ride list items too so the ride page can start loading the weather before the full ride has loaded.
export const getRideStartPosition = (ride: RideLike | RideOrRoute | RideListRide): Vec2 | undefined => {
  const startLatLng = 'start_latlng' in ride ? ride.start_latlng : undefined;
  // Strava sends an empty array for rides without gps data (eg indoor rides).
  if (startLatLng && startLatLng.length === 2) {
    return startLatLng;
  }
  const encodedPolyline = ride.map?.polyline || ride.map?.summary_polyline;
  if (!encodedPolyline) return undefined;
  return polyline.decode(encodedPolyline)[0] as Vec2 | undefined;
};

export const getSegmentPolylineFromRide = (ride: RideOrRoute, segment: RideOrRouteSegment) => {
  const encodedPolyline = getPolylineFromRide(ride);
  if (!encodedPolyline) return null;

  // NOTE: Don't want to use our own decodePolyline util here as we don't want to flip the lat and lng
  const decodedPolyline = polyline.decode(encodedPolyline);
  let startIndex, endIndex;
  let lastStartDist = Infinity;
  let lastEndDist = Infinity;

  decodedPolyline.forEach((step, i) => {
    const startDist = distance(step, segment.start_latlng);
    const endDist = distance(step, segment.end_latlng);
    if (startDist < lastStartDist) {
      startIndex = i;
      lastStartDist = startDist;
    }
    if (endDist < lastEndDist) {
      endIndex = i;
      lastEndDist = endDist;
    }
  });

  let segmentLine: Polyline = [segment.start_latlng, segment.end_latlng];
  let distanceToStart;
  if (startIndex !== undefined && endIndex !== undefined) {
    segmentLine = [...decodedPolyline].slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex));
    const lineToStart = [...decodedPolyline].slice(0, startIndex);
    distanceToStart = getPolylineDistance(lineToStart);
  }

  // Loop through the line at intervals and make sure there are enough points for
  // A smooth transition on the elevation graph
  segmentLine = resamplePolyline(segmentLine, SETTINGS.SEGMENT_RESAMPLE_INTERVAL_KMS);

  return { polyline: polyline.encode(segmentLine), distanceToStart };
};

export const makeRideLike = (ride: RideOrRouteOrSegment, windSpeed: number, parentRide?: RideOrRoute): RideLike => {
  if (isRideOrRouteSegment(ride) && !parentRide) {
    console.error(
      'Warning: Segment detected in makeRideLike function but no parentRide param detected. Ride polyline will be blank.',
    );
  }

  // TODO: get proper average speed of user here (maybe calc from all the other rides or just store the last average speed in storage)
  let userAverageSpeed: number = SETTINGS.DEFAULT_SETTINGS_DEFAULT_AVERAGE_SPEED;
  if (parentRide && !isRoute(parentRide)) {
    userAverageSpeed = metersPerSecondToKph(parentRide.average_speed);
  }

  const averageSpeed = isActivity(ride) ? ride.average_speed : kphToMetersPerSecond(userAverageSpeed);

  let segments = null;
  if (!isRideOrRouteSegment(ride)) {
    segments = getSegmentsFromRideOrRoute(ride);
  }

  let calories = 0;
  if (isActivity(ride)) {
    calories = ride.calories || 0;
  } else {
    // TODO: get user sex from actual api
    // TODO: test ride calories calculation or get new one.
    const timeInHours = metersToKm(ride.distance) / metersPerSecondToKph(averageSpeed);
    calories = calculateCalories('M', averageSpeed, windSpeed, timeInHours);
  }

  let map = {
    polyline: '',
  };
  let distance = ride.distance;
  let distanceToStart;
  if (isRideOrRouteSegment(ride)) {
    if (parentRide) {
      const segmentPolyline = getSegmentPolylineFromRide(parentRide, ride);
      map.polyline = segmentPolyline?.polyline || '';
      distanceToStart = segmentPolyline?.distanceToStart;
      // If a segment then use the length of the polyline as the distance not the value provided by strava.
      // This is because we are using out own polylines for the segment not the ones provided by strava.
      distance = getPolylineDistance(polyline.decode(map.polyline));
    }
  } else {
    map.polyline = getPolylineFromRide(ride) || '';
  }

  if (!distance && map.polyline) {
    distance = getPolylineDistance(polyline.decode(map.polyline));
  }

  const timeTakenInSeconds = isActivity(ride) ? ride.elapsed_time : distance / averageSpeed;

  let type: RideLike['type'] = 'ride';
  if (isRoute(ride)) {
    type = 'route';
  } else if (isRideOrRouteSegment(ride)) {
    type = 'segment';
  }

  let id: string = String(ride.id);
  if (isRoute(ride)) {
    id = ride.id_str ? ride.id_str : id;
  }

  return {
    id,
    type,
    name: ride.name,
    distance,
    distance_to_start: distanceToStart,
    average_speed: averageSpeed,
    elapsed_time: timeTakenInSeconds,
    calories,
    segments,
    map,
    private: !(ride.private === false),
  };
};

export const getRideHardness = (encodedPolyline: string | null, weather: WeatherLike) => {
  if (!encodedPolyline) {
    return 0;
  }

  const windDir = weather.windBearing;
  const windSpeed = weather.windSpeed;
  const maxWindSpeed = SETTINGS.HARDNESS_MAX_WIND_SPEED;

  const polylines: Polyline = decodePolylineForMapbox(encodedPolyline);

  const sections = processPolylines(polylines, windDir, windSpeed);

  // get wind speed hardness
  const windSpeedHardness = Math.min((windSpeed / maxWindSpeed) * 100, 100);

  // get wind dir hardness
  let windDirHardness = 0;
  for (const section of sections) {
    windDirHardness += section.hardness.dir || 0;
  }
  windDirHardness = windDirHardness / sections.length;

  // work out total hardness
  let hardness = ((windSpeedHardness * windDirHardness) / 10000) * 100;
  // console.log(hardness);
  hardness = (hardness / 100) * SETTINGS.HARDNESS_OUT_OF;
  // console.log(hardness);
  hardness = easeHardnessInOutQuad(hardness, 0, SETTINGS.HARDNESS_OUT_OF, SETTINGS.HARDNESS_OUT_OF);
  // console.log(hardness);
  hardness = Math.round(hardness * 10) / 10;
  // console.log(hardness);

  return hardness;
};

export const getHardnessColor = (hardness: number, min = 0, max = 0) => {
  let range = 100;
  if (max) {
    range = max - min;
    hardness = hardness - min;
  }
  // TODO: There seems to be 2 ways of mixing colors. Maybe combine into one at some point.
  // See 'getWindHardnessColor' in utils.ts
  // NOTE: This shows better results than using the below commented out interpolation function
  const color = Math.min(Math.abs(Math.round((255 / range) * hardness)), 255);
  const rgb: [number, number, number] = [255, 255 - color, color];
  return rgbToHex(rgb);
  // const factor = clamp(hardness / range, 0, 1);
  // const color = interpolateColors(colors.tailwind, colors.headwind, factor);
  // return color;
};

export const getElevationHardnessColor = (hardness: number, min = 0, max = 0) => {
  let range = 100;
  if (max) {
    range = max - min;
    hardness = hardness - min;
  }
  const color = Math.min(Math.abs(Math.round((255 / range) * hardness)), 255);
  const rgb: [number, number, number] = [color, 255 - color, 255];
  return rgbToHex(rgb);
  // const factor = clamp(hardness / range, 0, 1);
  // const color = interpolateColors(colors.elevationEasy, colors.elevationHard, factor);
  // return color;
};

export const processElevationPolylines = (path: Polyline, map: Map): ProcessedElevationPolylines => {
  const processed: ProcessedPolyline = [];
  let totalHardness = 0;
  const minInclinePercent = SETTINGS.HARDNESS_MIN_INCLINE;
  const maxInclinePercent = SETTINGS.HARDNESS_MAX_INCLINE;
  const inclineDiff = maxInclinePercent - minInclinePercent;
  let totalElevationGain = 0;
  let totalDistance = 0;

  const elevations: (number | null)[] = path.map(point => {
    return map.queryTerrainElevation(point, { exaggerated: false }) ?? null;
  });

  const hasElevationData = elevations.some(e => e !== null);
  if (!hasElevationData) {
    return {
      lines: [],
      hardness: 0,
      totalElevationGain: 0,
    };
  }

  path.forEach((point, i) => {
    const nextPoint = path[i + 1];
    const elevation = elevations[i];
    const nextElevation = elevations[i + 1];
    if (
      nextPoint &&
      elevation !== null &&
      elevation !== undefined &&
      nextElevation !== null &&
      nextElevation !== undefined
    ) {
      const dist = distance(point, nextPoint, { units: 'meters' });
      totalDistance += dist;

      const elevationChange = nextElevation - elevation;
      // Add up the total amount climbed
      if (elevationChange > 0) {
        totalElevationGain += elevationChange;
      }

      // The actual percentage of the slope
      let inclinePercent = dist > 0 ? (elevationChange / dist) * 100 : 0;
      // inclinePercent = elevation > nextElevation ? inclinePercent * -1 : inclinePercent;

      // Used to calculate the color. Maxes out at the min and max incline values
      let inclineColorPercent = ((inclinePercent - minInclinePercent) / inclineDiff) * 100;
      inclineColorPercent = clamp(inclineColorPercent, 0, 100);
      const color = getElevationHardnessColor(inclineColorPercent);

      // Hardness is how many sections are uphill
      let inclineHardness = clamp(inclinePercent, 0, maxInclinePercent);
      totalHardness += inclineHardness;

      processed.push({
        hardness: {
          incline: inclinePercent,
        },
        maxElevation: Math.max(elevation, nextElevation),
        distance: totalDistance,
        path: [point, nextPoint],
        color,
      });
    }
  });

  const maxHardness = maxInclinePercent * processed.length;

  // How many inclines?
  const inclineAmountHardness = SETTINGS.HARDNESS_OUT_OF * (totalHardness / maxHardness);

  // Average slope incline percent of whole ride
  const averageInclinePercent = totalDistance > 0 ? (totalElevationGain / totalDistance) * 100 : 0;
  const averageInclinePercentHardness =
    (SETTINGS.HARDNESS_OUT_OF * clamp(averageInclinePercent, 0, maxInclinePercent)) / maxInclinePercent;

  // Percentage of climbing in ride vs max climb setting
  const maxElevationGainHardness =
    (SETTINGS.HARDNESS_OUT_OF * clamp(totalElevationGain, 0, SETTINGS.HARDNESS_MAX_ELEVATION_GAIN)) /
    SETTINGS.HARDNESS_MAX_ELEVATION_GAIN;

  // The max elevation gain hardness has 3 entries in the hardness calc to weight it as more important.
  // Hardness score is combo of max elevation gain, average incline percent of the whole route and incline amount hardness
  // which is how many sections are up hill of the total sections.
  let hardness =
    SETTINGS.HARDNESS_OUT_OF *
    ((inclineAmountHardness +
      averageInclinePercentHardness +
      maxElevationGainHardness +
      maxElevationGainHardness +
      maxElevationGainHardness) /
      (SETTINGS.HARDNESS_OUT_OF * 5));

  if (isNaN(hardness)) {
    hardness = 0;
  }

  // Add some easing so it gets harder to get a higher score at the end
  hardness = easeHardnessInOutQuad(hardness, 0, SETTINGS.HARDNESS_OUT_OF, SETTINGS.HARDNESS_OUT_OF);

  return {
    lines: processed,
    hardness,
    totalElevationGain,
  };
};

export const processPolylines = (path: Polyline, windDir: number, windSpeed: number, map?: Map): ProcessedPolyline => {
  const lines = [];
  let lastPoint = null;
  let lastPxPoint = null;
  let elevations: (number | null)[] = [];
  if (map) {
    elevations = path.map(point => {
      return map.queryTerrainElevation(point, { exaggerated: false }) ?? null;
    });
  }
  let i = 0;
  for (const point of path) {
    let angle = 0;
    const pxPoint = latLngToPoint(point[1], point[0], 10000);
    if (lastPoint && lastPxPoint) {
      angle = getLineAngle(lastPxPoint, pxPoint);
      let windFactor = Math.abs(90 + windDir - angle);
      if (windFactor > 180) {
        windFactor = Math.abs(windFactor - 360);
      }
      const hardness = (windFactor / 180) * 100;
      const line: ProcessedPolylineItem = {
        color: getHardnessColor(hardness),
        path: [lastPoint, point],
        angle,
        hardness: {
          dir: hardness,
          speed: windSpeed,
        },
      };
      const elevation = elevations[i];
      if (elevation != null) {
        line.maxElevation = elevation;
      }
      lines.push(line);
    }
    lastPoint = point;
    lastPxPoint = pxPoint;
    i++;
  }
  return lines;
};

export interface OffsetPolylineResult {
  offsetPolyline: Polyline;
  originalPolyline?: Polyline;
  hasOffset: boolean;
}

export const createOffsetPolyline = (path: Polyline, offsetMeters = 10): OffsetPolylineResult => {
  if (!path || path.length < 2) {
    return { offsetPolyline: path, hasOffset: false };
  }

  const segments: {
    index: number;
    mid: Vec2;
    bearing: number;
  }[] = [];

  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i];
    const p2 = path[i + 1];
    segments.push({
      index: i,
      mid: [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2],
      bearing: bearing(p1, p2),
    });
  }

  const isReturn = new Array(segments.length).fill(false);
  const angleTolerance = 45;
  const distTolerance = 0.025; // 25 meters in km

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    // Check against earlier segments
    for (let j = 0; j < i; j++) {
      const earlier = segments[j];
      let diff = Math.abs(seg.bearing - earlier.bearing);
      if (diff > 180) diff = 360 - diff;

      if (diff > 180 - angleTolerance) {
        const d = distance(seg.mid, earlier.mid);
        if (d < distTolerance) {
          isReturn[i] = true;
          break;
        }
      }
    }
  }

  // Find contiguous blocks of return segments
  const blocks: { start: number; end: number }[] = [];
  let currentBlock: { start: number; end: number } | null = null;
  for (let i = 0; i < isReturn.length; i++) {
    if (isReturn[i]) {
      if (!currentBlock) {
        currentBlock = { start: i, end: i };
      } else {
        currentBlock.end = i;
      }
    } else {
      if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
    }
  }
  if (currentBlock) blocks.push(currentBlock);

  if (blocks.length === 0) {
    return {
      offsetPolyline: path,
      hasOffset: false,
    };
  }

  // Build the shifted continuous polyline
  const newPath: Polyline = [];
  let pathIdx = 0;

  for (const block of blocks) {
    // Add all untouched points before this block
    while (pathIdx <= block.start) {
      newPath.push(path[pathIdx]);
      pathIdx++;
    }

    // Offset the block of return segments using turf.lineOffset
    const blockCoords = path.slice(block.start, block.end + 2);
    const line = lineString(blockCoords);
    const offset = lineOffset(line, offsetMeters, { units: 'meters' });
    const offsetCoords = offset.geometry.coordinates as Vec2[];

    for (const pt of offsetCoords) {
      newPath.push(pt);
    }

    pathIdx = block.end + 2;
  }

  // Add any remaining untouched points
  while (pathIdx < path.length) {
    newPath.push(path[pathIdx]);
    pathIdx++;
  }

  return {
    offsetPolyline: newPath,
    originalPolyline: path,
    hasOffset: true,
  };
};

export const computeOffsets = (path: Polyline): number[] => {
  const offsets: number[] = new Array(path.length - 1).fill(0);
  const segments = [];
  const offset = 5;

  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i];
    const p2 = path[i + 1];
    const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
    const brg = bearing(p1, p2);
    segments.push({ index: i, mid, bearing: brg });
  }

  const gridSize = 0.0005;
  const grid = new globalThis.Map<string, number[]>();

  for (const seg of segments) {
    const gx = Math.floor(seg.mid[0] / gridSize);
    const gy = Math.floor(seg.mid[1] / gridSize);
    const key = `${gx},${gy}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key)!.push(seg.index);
  }

  const angleTolerance = 45;
  const distTolerance = 0.015;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const gx = Math.floor(seg.mid[0] / gridSize);
    const gy = Math.floor(seg.mid[1] / gridSize);

    let isObscured = false;

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${gx + dx},${gy + dy}`;
        const neighbors = grid.get(key);
        if (neighbors) {
          for (const neighborIdx of neighbors) {
            if (Math.abs(neighborIdx - i) < 5) continue;
            const neighbor = segments[neighborIdx];

            let diff = Math.abs(seg.bearing - neighbor.bearing);
            if (diff > 180) diff = 360 - diff;

            if (diff > 180 - angleTolerance) {
              const d = distance(seg.mid, neighbor.mid);
              if (d < distTolerance) {
                isObscured = true;
                break;
              }
            }
          }
        }
        if (isObscured) break;
      }
      if (isObscured) break;
    }

    if (isObscured) {
      offsets[i] = offset;
    }
  }

  return offsets;
};
